<template>
  <overlay-scrollbars
    ref="scrollRef"
    :options="scrollOptions"
    :events="scrollEvents"
    :class="[
      uuid,
      baseClass,
      allowDrag ? (isDragging ? 'cursor-grabbing select-none' : 'cursor-grab') : 'cursor-default'
    ]"
  >
    <div :class="containerClass">
      <slot />
    </div>
  </overlay-scrollbars>
</template>

<script lang="ts">
export default { name: 'OScroll' }
</script>

<script setup lang="ts">
import 'overlayscrollbars/styles/overlayscrollbars.css'
import { OverlayScrollbarsComponent as OverlayScrollbars } from 'overlayscrollbars-vue'
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-vue'
import type { EventListeners, OverflowBehavior } from 'overlayscrollbars'

const instance = getCurrentInstance()

interface Props {
  yAxis?: boolean
  xAxis?: boolean
  allowDrag?: boolean
  containerClass?: string
  baseClass?: string
  options?: Record<string, any>
  events?: EventListeners
}

const props = withDefaults(defineProps<Props>(), {
  yAxis: true,
  xAxis: false,
  allowDrag: false,
  containerClass: 'h-full',
  baseClass: 'h-full max-h-full w-full max-w-full',
  options: () => ({}),
  events: () => ({}),
})

const emit = defineEmits<{
  (e: 'scroll', value: boolean): void
}>()

const scrollRef = ref<OverlayScrollbarsComponentRef | null>(null)
const viewportRef = ref<HTMLElement | null>(null)

const uuid = 'scroll-' + (instance?.uid?.toString() || Math.random().toString())

const scrollOptions = computed(() => ({
  overflow: {
    x: (props.xAxis ? 'scroll' : 'hidden') as OverflowBehavior,
    y: (props.yAxis ? 'scroll' : 'hidden') as OverflowBehavior,
  },
  scrollbars: {
    ...props.options,
    autoHide: 'leave' as const,
  },
}))

const scrollEvents = computed(() => ({
  ...props.events,
}))

// Populate viewportRef once OverlayScrollbars initializes
onMounted(() => {
  nextTick(() => {
    viewportRef.value = getViewport()
  })
})

// Drag-to-scroll via pointer events on the viewport element
const { isDragging } = useDragScroll(viewportRef, computed(() => props.allowDrag))

watch(isDragging, (dragging) => {
  emit('scroll', dragging)
})

function getViewport(): HTMLElement | null {
  return scrollRef.value?.osInstance()?.elements().viewport ?? null
}

function scroll(target: { x?: number; y?: number } | HTMLElement, duration = 0) {
  const viewport = getViewport()
  if (!viewport) return

  if (target instanceof HTMLElement) {
    const viewportRect = viewport.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()

    const left = viewport.scrollLeft + (targetRect.left - viewportRect.left)
    const top = viewport.scrollTop + (targetRect.top - viewportRect.top)

    viewport.scrollTo({
      left,
      top,
      behavior: duration > 0 ? 'smooth' : 'auto',
    })
  } else {
    viewport.scrollTo({
      left: target.x,
      top: target.y,
      behavior: duration > 0 ? 'smooth' : 'auto',
    })
  }
}

function position() {
  const viewport = getViewport()
  if (viewport) {
    return { x: viewport.scrollLeft, y: viewport.scrollTop }
  }
}

function update() {
  const inst = scrollRef.value?.osInstance()
  if (inst) {
    return inst.update()
  }
}

function refresh() {
  const inst = scrollRef.value?.osInstance()
  if (inst) {
    inst.update()
  }
}

defineExpose({ scroll, position, update, refresh })
</script>
